import type {
  ContractFunction,
  ContractFunctionParameter,
} from '@mls-wallet/shared-types';
import type { AbiParameter } from 'viem';
import type { SelectedContractFunction } from './types';

/**
 * Find the exact ABI overload identified by calldata or policy metadata.
 * Function names are not unique when a contract exposes overloads, but their
 * four-byte selectors are.
 */
export function findContractFunctionBySelector(
  functions: ContractFunction[],
  selector: string
): ContractFunction | undefined {
  return functions.find(
    (func) => func.selector.toLowerCase() === selector.toLowerCase()
  );
}

/**
 * Rebuild a viem ABI parameter from backend metadata, including numeric widths,
 * fixed byte lengths, and nested tuple components.
 */
export function toViemAbiParameter(
  parameter: ContractFunctionParameter
): AbiParameter {
  const components =
    'components' in parameter && parameter.components
      ? parameter.components.map(toViemAbiParameter)
      : undefined;

  const solidityType = (() => {
    if ('bitSize' in parameter) {
      return `${parameter.type}${parameter.bitSize}`;
    }
    if ('bytesLength' in parameter && parameter.bytesLength !== 'dynamic') {
      return `bytes${parameter.bytesLength}`;
    }
    return parameter.type;
  })();

  return {
    name: parameter.name,
    type: solidityType,
    ...(components ? { components } : {}),
  };
}

/** Convert one backend ABI function into the builder's selected-function shape. */
export function toSelectedContractFunction(
  func: ContractFunction
): SelectedContractFunction {
  return {
    selector: func.selector,
    name: func.name,
    inputs: func.inputs.map(toViemAbiParameter),
    stateMutability: func.stateMutability,
  };
}
